import * as React from "react";
import {
  Typography,
  Box,
  MenuItem,
  Button,
  Switch,
  TextField,
  Input,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";

import reorder from "../../../../public/assets/icons/reorder.svg";
import cancel from "../../../../public/assets/icons/cancel.svg";
import search from "../../../../public/assets/icons/search.svg";
import { scrollStyles } from "../../../theme/Theme";
import { styled } from "@mui/material/styles";

type Props = {
  anchorEl: any;
  options: Option[];
  selected?: any;
  handleRenameColumnName?: any;
  values: Option[];
  setValues: Function;
  disabled: boolean;
};
type Option = {
  id: string;
  field: string;
  headerName: string;
  hide: boolean;
  isMeta?: boolean;
};

const StyledButton = styled(Button)({
  borderRadius: "4px",
  textTransform: "none",
  flexGrow: 1,
});

export const FieldsMenu = ({
  anchorEl,
  options,
  selected,
  handleRenameColumnName,
  values,
  setValues,
  disabled,
  ...props
}: Props) => {

  //region State
  const [searchValue, setSearchValue] = React.useState<string>("");
  const dragItem = React.useRef();
  const dragOverItem = React.useRef();
  const [renameLabelOfField, setRenameLabelOfField] =
    React.useState<string>("");
  const [optionsDict, setOptionsDict] = React.useState<any>({});
  const [fields, setFields] = React.useState(values);
  //endregion

  //region Helper functions
  const dragStart = (e, position) => {
    dragItem.current = position;
  };
  const dragEnter = (e, position) => {
    dragOverItem.current = position;
  };
  const drop = (e) => {
    const copyOptions = [...values];
    //@ts-ignore
    const dragItemContent = copyOptions[dragItem.current];
    copyOptions.splice(dragItem.current, 1);
    copyOptions.splice(dragOverItem.current, 0, dragItemContent);
    dragItem.current = null;
    dragOverItem.current = null;
    // setOptions(copyOptions);
    setValues(copyOptions);
    // onOrderChange && onOrderChange(copyOptions);
  };
  const handleKeyPress = (e, id, newValue) => {
    if (e.key === "Enter") {
      handleRenameColumnName(id, newValue);
      setRenameLabelOfField("");
    }
  };
  const handleChangeTextField = (e, fieldName: any) => {
    const value = e.target.value;
    const dict = { ...optionsDict };
    dict[fieldName] = value;
    setOptionsDict(dict);
  };
  const renderName = (option: any) => {
    if (option?.field === renameLabelOfField) {
      return (
          <TextField
              disabled={disabled}
              value={optionsDict[option?.field]}
              onChange={(e) => handleChangeTextField(e, option?.field)}
              onKeyPress={(e) => {
                handleKeyPress(e, option?.id, optionsDict[option?.field]);
              }}
              size="small"
          />
      );
    } else {
      return (
          <Typography variant="body1" sx={{ color: "artisticTextHigh.main" }}>
            {option?.headerName}
          </Typography>
      );
    }
  };
  const handleSearch = (e) => {
    const value = e.target.value?.toLowerCase();
    setSearchValue(value);
    const l = values?.filter((option) =>
        option?.headerName?.toLowerCase().includes(value)
    );
    setFields(l);
  };
  const handleClearSearch = () => {
    setSearchValue("");
    setFields(values);
  };
  const hideAllFields = () => {
    const cols = values?.map((column) => {
      return { ...column, hide: true };
    });
    setValues(cols);
  };
  const showAllFields = () => {
    const cols = values?.map((column) => {
      return { ...column, hide: false };
    });
    setValues(cols);
  };
  const hideColumns = (option) => {
    const cols = values?.map((column) => {
      if (column.field === option.field) {
        // if (column.hide) {
        //   setHiddenNumberOfFields(hiddenNumberOfFields - 1);
        // } else {
        //   setHiddenNumberOfFields(hiddenNumberOfFields + 1);
        // }
        return { ...column, hide: !column.hide };
      }
      return column;
    });
    setValues(cols);
  };
  //endregion

  //region Lifecycle
  React.useEffect(() => {
    if (searchValue?.length) {
      const l = values?.filter((option) =>
        option?.headerName?.toLowerCase().includes(searchValue)
      );
      setFields(l);
    } else {
      setFields(values);
    }
    const dict = {};
    values?.map((option) => {
      dict[option?.field] = option?.headerName;
    });
    setOptionsDict(dict);
  }, [values]);

  React.useEffect(() => {
    setFields(values);
  }, []);
  //endregion

  const content = (option) => {
    return (
      <Box display="flex" flexDirection="column" rowGap="5px">
        <Typography variant="caption1">{option?.field}</Typography>
        <Typography variant="overline">{option?.description}</Typography>
      </Box>
    );
  };

  const renderedFields = React.useMemo(() => {
    return (
        <>
          {fields
              ?.filter((option) => !option.isMeta)
              ?.map((option, index) => (
                  <MenuItem
                      disabled={disabled}
                      key={option?.field + index}
                      sx={{
                        ":hover": {
                          backgroundColor: "transparent",
                        },
                        padding: "8px 16px",
                        gap: "8px",
                      }}
                      selected={option.field === selected?.field}
                      disableRipple
                      value={option?.field}
                      onDragStart={(e) => dragStart(e, index)}
                      onDragEnter={(e) => dragEnter(e, index)}
                      onDragEnd={drop}
                      draggable
                      onKeyDown={(e) => e.stopPropagation()}
                  >
                    <Tooltip title={content(option)} arrow placement="right">
                      <Box
                          sx={{
                            display: "flex",
                            width: "400px",
                            gap: "5px",
                            justifyContent: "space-between",
                          }}
                      >
                        <img src={reorder} alt="reorder" />
                        <Button
                            disabled={disabled}
                            sx={{ justifyContent: "start", width: "100%" }}
                            disableRipple
                            onDoubleClick={() => {
                              if (option?.id) {
                                setRenameLabelOfField(option?.field);
                              }
                            }}
                        >
                          {renderName(option)}
                        </Button>
                      </Box>
                    </Tooltip>
                    <Switch
                        disabled={disabled}
                        defaultChecked
                        size="small"
                        checked={!option?.hide}
                        onChange={() => hideColumns(option)}
                    />
                  </MenuItem>
              ))}
        </>
    );
  }, [fields]);

  return (
    <>
      <Input
        sx={{
          padding: "0px 0px 0px 0px",
          margin: "15px 15px 0px 15px",
          width: `calc(100% - 30px)`,
          height: "fit-content",
        }}
        disabled={disabled}
        disableUnderline
        value={searchValue}
        placeholder="Find a field"
        onChange={handleSearch}
        onKeyDown={(e) => e.stopPropagation()}
        endAdornment={
          searchValue?.length ? (
            <IconButton onClick={handleClearSearch} disableRipple={true}>
              <img src={cancel} alt="cancel" style={{ height: "14px" }} />
            </IconButton>
          ) : (
            <IconButton disableRipple={true}>
              <img src={search} alt="search" style={{ height: "14px" }} />
            </IconButton>
          )
        }
      />

      <Divider
        orientation="horizontal"
        flexItem
        sx={{
          border: `1px solid #cfd1d0`,
          ml: "15px",
          mr: "15px",
          mb: "8px",
        }}
      />

      <Box
        sx={{
          maxHeight: "350px",
          overflow: "auto"
        }}
      >
        {renderedFields}
      </Box>

      {searchValue?.length ? (
        <></>
      ) : (
        <Box
          display="flex"
          flexDirection="row"
          columnGap="10px"
          margin="8px 15px 2px"
        >
          <StyledButton disabled={disabled} variant="outlined" onClick={hideAllFields}>
            Hide All
          </StyledButton>
          <StyledButton disabled={disabled} variant="outlined" onClick={showAllFields}>
            Show All
          </StyledButton>
        </Box>
      )}
    </>
  );
};
